"use client";

import { useEffect, useRef, useState } from "react";
import { CHAOS_WHEELS, WheelOption } from "@/data/wheels";
import { CHAOS_TYPE_WHEEL, ChaosOutcome } from "@/lib/chaosEngine";
import { ChaosOverlayStage } from "@/hooks/useGameState";
import SpinningWheel from "@/components/SpinningWheel";

interface ChaosWheelOverlayProps {
  stage: ChaosOverlayStage;
  outcome: ChaosOutcome | null;
  typeSpinToken: number;
  resultSpinToken: number;
  onTypeSpinEnd: () => void;
  onResultSpinEnd: () => void;
  onDismiss: () => void;
  onTick: (strength: number) => void;
}

export default function ChaosWheelOverlay({
  stage,
  outcome,
  typeSpinToken,
  resultSpinToken,
  onTypeSpinEnd,
  onResultSpinEnd,
  onDismiss,
  onTick,
}: ChaosWheelOverlayProps) {
  // Each stage's SpinningWheel only reveals its result face once its own
  // spin lands, even though `stage` flips the moment the spin is requested.
  const [typeLanded, setTypeLanded] = useState(false);
  const [resultLanded, setResultLanded] = useState(false);
  const [countdown, setCountdown] = useState(3);

  useEffect(() => {
    setTypeLanded(false);
  }, [typeSpinToken]);

  useEffect(() => {
    setResultLanded(false);
  }, [resultSpinToken]);

  // Once the result lands, auto-dismiss (applying its effect) after 3s.
  const onDismissRef = useRef(onDismiss);
  onDismissRef.current = onDismiss;

  useEffect(() => {
    if (!resultLanded) return;
    setCountdown(3);
    const start = Date.now();
    const interval = setInterval(() => {
      const remaining = Math.ceil((3000 - (Date.now() - start)) / 1000);
      setCountdown(Math.max(0, remaining));
    }, 200);
    const timeout = setTimeout(() => onDismissRef.current(), 3000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [resultLanded]);

  if (!stage || !outcome) return null;

  const typeOption =
    CHAOS_TYPE_WHEEL.options.find((o) => o.name === outcome.event.type) ??
    CHAOS_TYPE_WHEEL.options[0];

  const resultWheel = CHAOS_WHEELS.find((w) => w.name === outcome.event.type);
  const resultOption: WheelOption =
    resultWheel?.options.find((o) => o.name === outcome.event.result) ?? {
      name: outcome.event.result,
      value: outcome.event.pointImpact,
    };

  const handleTypeSpinEnd = () => {
    setTypeLanded(true);
    onTypeSpinEnd();
  };

  const handleResultSpinEnd = () => {
    setResultLanded(true);
    onResultSpinEnd();
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col items-center justify-center gap-6 bg-black/85 px-4 backdrop-blur-sm">
      <h2 className="animate-chaos-title pl-[0.2em] text-center text-[clamp(1.75rem,8vw,6rem)] font-black uppercase tracking-[0.2em] text-yellow-400 [text-shadow:0_0_30px_rgba(250,204,21,0.6)] md:pl-[0.3em] md:tracking-[0.3em]">
        Chaos Wheel
      </h2>

      {stage === "type" && (
        <div className="animate-drop-in">
          <SpinningWheel
            wheel={CHAOS_TYPE_WHEEL}
            spinToken={typeSpinToken}
            resultOption={typeOption}
            onSpinEnd={handleTypeSpinEnd}
            onTick={onTick}
            showResult={typeLanded}
            showValue={false}
          />
        </div>
      )}

      {stage === "result" && resultWheel && (
        <div className="animate-drop-in flex flex-col items-center gap-6">
          <SpinningWheel
            wheel={resultWheel}
            spinToken={resultSpinToken}
            resultOption={resultOption}
            onSpinEnd={handleResultSpinEnd}
            onTick={onTick}
            showResult={resultLanded}
            showValue={false}
          />

          {resultLanded && (
            <div className="animate-slide-down w-full max-w-md rounded-2xl border border-yellow-400/40 bg-black/60 p-6 text-center">
              <div className="mb-1 text-xs font-bold uppercase tracking-[0.3em] text-yellow-300">
                {outcome.event.type}: {outcome.event.result}
              </div>
              <p className="mb-4 text-sm text-white/80">{outcome.event.description}</p>
              <div className="text-xs font-bold uppercase tracking-widest text-white/40">
                Applying in {countdown}s
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
